import React, { useState, useEffect, useMemo } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Filter, Check, ChevronsUpDown } from "lucide-react";
import { format } from "date-fns";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function ErrorTracking({ user }) {
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorMaster, setErrorMaster] = useState([]);
  const [projects, setProjects] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Reports data
  const [typeReport, setTypeReport] = useState([]);
  const [projectReport, setProjectReport] = useState([]);
  const [estimationReport, setEstimationReport] = useState([]);
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    project_id: "",
    estimation_id: "",
    assigned_to: "",
    error_source: "", // Internal or External
    work_type: "",
    error_master_id: "",
    quantity: "",
    rework_hours: "",
    impact: "" // New field for Estimation errors: Low, Medium, High
  });
  
  // Selected error master for auto-population
  const [selectedErrorMaster, setSelectedErrorMaster] = useState(null);
  
  // Combobox open states
  const [openUser, setOpenUser] = useState(false);
  const [openProject, setOpenProject] = useState(false);
  const [openEstimation, setOpenEstimation] = useState(false);
  const [openError, setOpenError] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    project: "",
    user: "",
    work_type: "",
    error_type: ""
  });

  const isManager = user?.department === "Management";
  
  // Get selected user's department to determine which list to show
  const selectedUserDepartment = useMemo(() => {
    if (!formData.assigned_to) return null;
    const selectedUser = users.find(u => u.id === formData.assigned_to);
    return selectedUser?.department || null;
  }, [formData.assigned_to, users]);
  
  // Determine if we should show estimations or projects based on selected user
  const showEstimationsList = selectedUserDepartment === "Estimation";
  
  // Filter assignable people based on work type
  // For Client Feedback: use employees (from employees collection)
  // For Estimation: use users from Estimation department
  // For other work types: use users from Projects department
  const filteredUsers = useMemo(() => {
    const isClientFeedback = formData.work_type === "Client Feedback";
    const isEstimation = formData.work_type === "Estimation";
    
    if (isClientFeedback) {
      // Use employees for Client Feedback errors
      return employees.map(e => ({
        id: e.id,
        first_name: e.first_name,
        last_name: e.last_name,
        department: e.department
      }));
    } else if (isEstimation) {
      // Use users from Estimation department for Estimation errors
      let filtered = users.filter(u => u.department === "Estimation");
      
      // If editing and assigned user is not in filtered list, add them
      if (editingLog && formData.assigned_to) {
        const assignedUserInList = filtered.find(u => u.id === formData.assigned_to);
        if (!assignedUserInList) {
          const assignedUser = users.find(u => u.id === formData.assigned_to);
          if (assignedUser) {
            filtered = [...filtered, assignedUser];
          }
        }
      }
      return filtered;
    } else {
      // Use users from Projects department for other error types
      let filtered = users.filter(u => u.department === "Projects");
      
      // If editing and assigned user is not in filtered list, add them
      if (editingLog && formData.assigned_to) {
        const assignedUserInList = filtered.find(u => u.id === formData.assigned_to);
        if (!assignedUserInList) {
          const assignedUser = users.find(u => u.id === formData.assigned_to);
          if (assignedUser) {
            filtered = [...filtered, assignedUser];
          }
        }
      }
      return filtered;
    }
  }, [users, employees, formData.work_type, formData.assigned_to, editingLog]);
  
  // Filter users for assignment - show Projects and Estimation department users
  const assignableUsers = useMemo(() => {
    return users.filter(u => u.department === "Projects" || u.department === "Estimation" || u.department === "Management");
  }, [users]);
  
  // Filter error master based on selected work_type
  const filteredErrorMaster = errorMaster.filter(master => {
    if (!formData.work_type) return true; // Show all if no work type selected
    // Show errors that have the selected work_type in their work_types array
    return master.work_types && master.work_types.includes(formData.work_type);
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Use the dedicated assignable-users endpoint instead of /users and /employees
      // This endpoint doesn't require /users view permission
      const [logsRes, masterRes, projectsRes, estimationsRes, assignableUsersRes, typeReportRes, projectReportRes] = await Promise.all([
        api.get("/error-logs"),
        api.get("/error-master"),
        api.get("/projects"),
        api.get("/estimations"),
        api.get("/error-logs/assignable-users"),
        api.get("/error-logs/reports/by-type"),
        api.get("/error-logs/reports/by-project")
      ]);
      setErrorLogs(logsRes.data);
      setErrorMaster(masterRes.data);
      setProjects(projectsRes.data);
      setEstimations(estimationsRes.data);
      setUsers(assignableUsersRes.data.users || []);
      setEmployees(assignableUsersRes.data.employees || []);
      setTypeReport(typeReportRes.data);
      setProjectReport(projectReportRes.data);
      
      // Fetch estimation report
      try {
        const estimationReportRes = await api.get("/error-logs/reports/by-estimation");
        setEstimationReport(estimationReportRes.data);
      } catch (e) {
        // Endpoint might not exist yet, ignore
        console.log("Estimation report endpoint not available");
      }
    } catch (error) {
      console.error("Error loading data:", error.response?.data);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data with proper type conversion
      const submitData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        rework_hours: formData.rework_hours ? parseFloat(formData.rework_hours) : 0,
        // For estimation errors, include impact field
        impact: formData.work_type === "Estimation" ? formData.impact : null
      };
      
      // Clear project_id if estimation is selected and vice versa
      if (formData.work_type === "Estimation") {
        submitData.project_id = null;
      } else {
        submitData.estimation_id = null;
      }
      
      if (editingLog) {
        await api.put(`/error-logs/${editingLog.id}`, submitData);
        toast.success("Error log updated successfully");
      } else {
        await api.post("/error-logs", submitData);
        toast.success("Error assigned successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving error log:", error.response?.data);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || "Failed to save error log";
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleEdit = (log) => {
    setEditingLog(log);
    // Determine error_source based on work_type
    const errorSource = log.work_type === "Client Feedback" ? "External" : "Internal";
    setFormData({
      project_id: log.project_id || "",
      estimation_id: log.estimation_id || "",
      assigned_to: log.assigned_to,
      error_source: errorSource,
      work_type: log.work_type,
      error_master_id: log.error_master_id,
      quantity: log.quantity.toString(),
      rework_hours: log.rework_hours ? log.rework_hours.toString() : "",
      impact: log.impact || ""
    });
    
    // Find and set selected error master
    const master = errorMaster.find(m => m.id === log.error_master_id);
    setSelectedErrorMaster(master);
    
    setDialogOpen(true);
  };

  const handleDelete = async (logId) => {
    if (!confirm("Are you sure you want to delete this error log?")) return;
    try {
      await api.delete(`/error-logs/${logId}`);
      toast.success("Error log deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete error log");
    }
  };

  const resetForm = () => {
    setEditingLog(null);
    setSelectedErrorMaster(null);
    setFormData({
      project_id: "",
      estimation_id: "",
      assigned_to: "",
      error_source: "",
      work_type: "",
      error_master_id: "",
      quantity: "",
      rework_hours: "",
      impact: ""
    });
  };

  // Handle user selection - auto-set work type based on department
  const handleUserSelect = (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    const isEstimationDept = selectedUser?.department === "Estimation";
    
    setFormData(prev => ({
      ...prev,
      assigned_to: userId,
      // Auto-set work type to Estimation if user is from Estimation department
      work_type: isEstimationDept ? "Estimation" : prev.work_type,
      // Default error source to Internal
      error_source: prev.error_source || "Internal",
      // Clear project/estimation when user changes
      project_id: isEstimationDept ? "" : prev.project_id,
      estimation_id: isEstimationDept ? prev.estimation_id : "",
      // Clear error master when work type changes
      error_master_id: isEstimationDept && prev.work_type !== "Estimation" ? "" : prev.error_master_id
    }));
    
    if (isEstimationDept && formData.work_type !== "Estimation") {
      setSelectedErrorMaster(null);
    }
  };

  const handleErrorMasterChange = (masterId) => {
    const master = errorMaster.find(m => m.id === masterId);
    setSelectedErrorMaster(master);
    setFormData({ ...formData, error_master_id: masterId });
  };

  const calculateTotalValue = () => {
    if (!formData.quantity || !selectedErrorMaster) return 0;
    // Find the error type to get weightage
    return parseFloat(formData.quantity) * (selectedErrorMaster.weightage_points || 0);
  };

  const clearFilters = () => {
    setFilters({
      project: "",
      user: "",
      work_type: "",
      error_type: ""
    });
  };

  // Filter error logs
  const filteredLogs = errorLogs.filter(log => {
    if (filters.project && log.project_id !== filters.project) return false;
    if (filters.user && log.assigned_to !== filters.user) return false;
    if (filters.work_type && log.work_type !== filters.work_type) return false;
    if (filters.error_type && log.error_type_id !== filters.error_type) return false;
    return true;
  });

  // Get unique error types from logs
  const uniqueErrorTypes = [...new Set(errorLogs.map(log => log.error_type_id))].map(id => {
    const log = errorLogs.find(l => l.error_type_id === id);
    return { id, name: log?.error_type_name || 'Unknown' };
  }).filter(type => type.id); // Filter out any undefined IDs

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-md3-display-small">Error Tracking</h1>
          <p className="text-md3-on-surface-variant">Track and manage project errors</p>
        </div>

        <Tabs defaultValue="logs" className="w-full">
          <TabsList>
            <TabsTrigger value="logs">All Error Logs</TabsTrigger>
            <TabsTrigger value="reports-projects">Reports - Projects</TabsTrigger>
            <TabsTrigger value="reports-estimation">Reports - Estimation</TabsTrigger>
          </TabsList>

          {/* All Error Logs Tab */}
          <TabsContent value="logs" className="space-y-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-md3-title-large text-md3-on-surface ">Error Logs ({filteredLogs.length})</CardTitle>
                  <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Assign Error
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="p-4 bg-md3-surface rounded-md3-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md3-body-small font-semibold text-md3-on-surface-variant">Filters</h3>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"  onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Project</Label>
                      <Select value={filters.project || "all"} onValueChange={(value) => setFilters({ ...filters, project: value === "all" ? "" : value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="All Projects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Projects</SelectItem>
                          {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.project_number} - {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Assigned To</Label>
                      <Select value={filters.user || "all"} onValueChange={(value) => setFilters({ ...filters, user: value === "all" ? "" : value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="All Users" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Work Type</Label>
                      <Select value={filters.work_type || "all"} onValueChange={(value) => setFilters({ ...filters, work_type: value === "all" ? "" : value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="Modelling">Modelling</SelectItem>
                          <SelectItem value="Shop">Shop</SelectItem>
                          <SelectItem value="Erection">Erection</SelectItem>
                          <SelectItem value="Checking">Checking</SelectItem>
                          <SelectItem value="Client Feedback">Client Feedback</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Error Type</Label>
                      <Select value={filters.error_type || "all"} onValueChange={(value) => setFilters({ ...filters, error_type: value === "all" ? "" : value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="All Error Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Error Types</SelectItem>
                          {uniqueErrorTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="bg-md3-surface-container rounded-md3-md">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Project</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead>Work Type</TableHead>
                          <TableHead>Error Description</TableHead>
                          <TableHead>Error Type</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Total Value</TableHead>
                          <TableHead>Rework Hrs</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center text-md3-on-surface-variant">
                              No error logs found
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell>{format(new Date(log.created_at), "MMM dd, yyyy")}</TableCell>
                              <TableCell className="max-w-[200px]">
                                <div className="text-md3-body-small">
                                  <div className="font-medium">{log.project_number}</div>
                                  <div className="text-md3-on-surface-variant truncate" title={log.project_name}>{log.project_name}</div>
                                </div>
                              </TableCell>
                              <TableCell>{log.assigned_to_name}</TableCell>
                              <TableCell>{log.work_type}</TableCell>
                              <TableCell>{log.error_description}</TableCell>
                              <TableCell>{log.error_type_name}</TableCell>
                              <TableCell>{log.quantity || 0}</TableCell>
                              <TableCell className="font-semibold">{(log.total_value || 0).toFixed(2)}</TableCell>
                              <TableCell>{log.rework_hours || 0}</TableCell>
                              <TableCell className="text-right space-x-2">
                                {isManager && (
                                  <>
                                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                                       onClick={() => handleEdit(log)}>
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                                       onClick={() => handleDelete(log.id)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports - Projects Tab */}
          <TabsContent value="reports-projects" className="space-y-6">
            {/* Error Type Summary */}
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Error Type Summary (Projects)</CardTitle>
                <CardDescription>Errors grouped by type showing total impact for project work</CardDescription>
              </CardHeader>
              <CardContent>
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Error Type</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead>Total Quantity</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Total Rework Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {typeReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-md3-on-surface-variant">
                          No data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      typeReport.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.error_type}</TableCell>
                          <TableCell>{item.count}</TableCell>
                          <TableCell>{item.total_quantity.toFixed(2)}</TableCell>
                          <TableCell className="font-semibold">{item.total_value.toFixed(2)}</TableCell>
                          <TableCell>{item.total_rework_hours.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Project Summary */}
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Project Summary</CardTitle>
                <CardDescription>Errors grouped by project showing total impact</CardDescription>
              </CardHeader>
              <CardContent>
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Number</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Error Count</TableHead>
                      <TableHead>Total Quantity</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Total Rework Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-md3-on-surface-variant">
                          No data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      projectReport.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.project_number}</TableCell>
                          <TableCell>{item.project_name}</TableCell>
                          <TableCell>{item.count}</TableCell>
                          <TableCell>{item.total_quantity.toFixed(2)}</TableCell>
                          <TableCell className="font-semibold">{item.total_value.toFixed(2)}</TableCell>
                          <TableCell>{item.total_rework_hours.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports - Estimation Tab */}
          <TabsContent value="reports-estimation" className="space-y-6">
            {/* Estimation Summary */}
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Estimation Summary</CardTitle>
                <CardDescription>Errors grouped by estimation showing total impact</CardDescription>
              </CardHeader>
              <CardContent>
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estimation Number</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Error Count</TableHead>
                      <TableHead>Total Quantity</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Impact Distribution</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {estimationReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-md3-on-surface-variant">
                          No estimation error data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      estimationReport.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.estimation_number}</TableCell>
                          <TableCell>{item.project_name}</TableCell>
                          <TableCell>{item.count}</TableCell>
                          <TableCell>{item.total_quantity?.toFixed(2) || 0}</TableCell>
                          <TableCell className="font-semibold">{item.total_value?.toFixed(2) || 0}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {item.high_count > 0 && <Badge variant="destructive" className="text-xs">High: {item.high_count}</Badge>}
                              {item.medium_count > 0 && <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">Medium: {item.medium_count}</Badge>}
                              {item.low_count > 0 && <Badge variant="outline" className="text-xs">Low: {item.low_count}</Badge>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Assign/Edit Error Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editingLog ? "Edit Error Log" : "Assign Error"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editingLog ? "Update the error log details" : "Create a new error assignment"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 overflow-y-auto flex-1 pr-2">
              {/* Row 1: Assigned To (User) - FIRST */}
              <div>
                <Label htmlFor="assigned_to" className="text-md3-label-large text-gray-700 font-medium">Assigned To*</Label>
                <Popover open={openUser} onOpenChange={setOpenUser}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full w-full justify-between overflow-hidden" role="combobox"
                      aria-expanded={openUser}
                      title={formData.assigned_to && assignableUsers.find((u) => u.id === formData.assigned_to)
                        ? `${assignableUsers.find((u) => u.id === formData.assigned_to).first_name} ${assignableUsers.find((u) => u.id === formData.assigned_to).last_name}`
                        : ""}
                    >
                      <span className="truncate">
                        {formData.assigned_to
                          ? assignableUsers.find((u) => u.id === formData.assigned_to)
                              ? `${assignableUsers.find((u) => u.id === formData.assigned_to).first_name} ${assignableUsers.find((u) => u.id === formData.assigned_to).last_name} (${assignableUsers.find((u) => u.id === formData.assigned_to).department})`
                              : "Select user..."
                          : "Select user..."}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0">
                    <Command>
                      <CommandInput placeholder="Search user..." />
                      <CommandEmpty>No user found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {assignableUsers.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={`${u.first_name} ${u.last_name}`}
                            onSelect={() => {
                              handleUserSelect(u.id);
                              setOpenUser(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.assigned_to === u.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {u.first_name} {u.last_name} ({u.department})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Row 2: Project OR Estimation based on user department - SECOND */}
              {formData.assigned_to && (
                <div>
                  {showEstimationsList ? (
                    <>
                      <Label htmlFor="estimation" className="text-md3-label-large text-gray-700 font-medium">Estimation*</Label>
                      <Popover open={openEstimation} onOpenChange={setOpenEstimation}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full w-full justify-between overflow-hidden" role="combobox"
                            aria-expanded={openEstimation}
                            title={formData.estimation_id && estimations.find((e) => e.id === formData.estimation_id)
                              ? `${estimations.find((e) => e.id === formData.estimation_id).estimation_number} - ${estimations.find((e) => e.id === formData.estimation_id).project_name}`
                              : ""}
                          >
                            <span className="truncate">
                              {formData.estimation_id
                                ? estimations.find((e) => e.id === formData.estimation_id)
                                    ? `${estimations.find((e) => e.id === formData.estimation_id).estimation_number} - ${estimations.find((e) => e.id === formData.estimation_id).project_name}`
                                    : "Select estimation..."
                                : "Select estimation..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-0">
                          <Command>
                            <CommandInput placeholder="Search estimation..." />
                            <CommandEmpty>No estimation found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {estimations.map((estimation) => (
                                <CommandItem
                                  key={estimation.id}
                                  value={`${estimation.estimation_number} ${estimation.project_name}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, estimation_id: estimation.id, project_id: "" });
                                    setOpenEstimation(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.estimation_id === estimation.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {estimation.estimation_number} - {estimation.project_name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </>
                  ) : (
                    <>
                      <Label htmlFor="project" className="text-md3-label-large text-gray-700 font-medium">Project*</Label>
                      <Popover open={openProject} onOpenChange={setOpenProject}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full w-full justify-between overflow-hidden" role="combobox"
                            aria-expanded={openProject}
                            title={formData.project_id && projects.find((p) => p.id === formData.project_id)
                              ? `${projects.find((p) => p.id === formData.project_id).project_number} - ${projects.find((p) => p.id === formData.project_id).name}`
                              : ""}
                          >
                            <span className="truncate">
                              {formData.project_id
                                ? projects.find((p) => p.id === formData.project_id)
                                    ? `${projects.find((p) => p.id === formData.project_id).project_number} - ${projects.find((p) => p.id === formData.project_id).name}`
                                    : "Select project..."
                                : "Select project..."}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-0">
                          <Command>
                            <CommandInput placeholder="Search project..." />
                            <CommandEmpty>No project found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {projects.map((project) => (
                                <CommandItem
                                  key={project.id}
                                  value={`${project.project_number} ${project.name}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, project_id: project.id, estimation_id: "" });
                                    setOpenProject(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.project_id === project.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {project.project_number} - {project.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </>
                  )}
                </div>
              )}

              {/* Row 3: Error Source */}
              <div>
                <Label htmlFor="error_source" className="text-md3-label-large text-gray-700 font-medium">Error Source*</Label>
                <Select
                  value={formData.error_source}
                  onValueChange={(value) => {
                    // Reset work_type and error_master when source changes (unless Estimation work type)
                    const isEstimation = formData.work_type === "Estimation";
                    const newWorkType = value === "External" ? "Client Feedback" : (isEstimation ? "Estimation" : "");
                    setFormData({ 
                      ...formData, 
                      error_source: value, 
                      work_type: newWorkType,
                      error_master_id: isEstimation ? formData.error_master_id : "" 
                    });
                    if (!isEstimation) setSelectedErrorMaster(null);
                  }}
                  required
                >
                  <SelectTrigger id="error_source">
                    <SelectValue placeholder="Select error source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="External">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Row 4: Work Type */}
              <div>
                <Label htmlFor="work_type" className="text-md3-label-large text-gray-700 font-medium">Work Type*</Label>
                {formData.work_type === "Estimation" ? (
                  // Estimation - Auto-set (read-only display)
                  <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                    Estimation
                  </div>
                ) : formData.error_source === "External" ? (
                  // External - Auto-set to Client Feedback (read-only display)
                  <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                    Client Feedback
                  </div>
                ) : (
                  // Internal - Show dropdown with internal work types
                  <Select
                    value={formData.work_type}
                    onValueChange={(value) => {
                      setFormData({ ...formData, work_type: value, error_master_id: "" });
                      setSelectedErrorMaster(null);
                    }}
                    required
                    disabled={!formData.error_source}
                  >
                    <SelectTrigger id="work_type">
                      <SelectValue placeholder={formData.error_source ? "Select work type" : "Select error source first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Modelling">Modelling</SelectItem>
                      <SelectItem value="Shop">Shop</SelectItem>
                      <SelectItem value="Erection">Erection</SelectItem>
                      <SelectItem value="Checking">Checking</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="error_description" className="text-md3-label-large text-gray-700 font-medium">Error Description*</Label>
                <Popover open={openError} onOpenChange={setOpenError}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full w-full justify-between" role="combobox"
                      aria-expanded={openError}
                      
                      disabled={!formData.work_type}
                    >
                      {formData.error_master_id
                        ? errorMaster.find((m) => m.id === formData.error_master_id)?.description || "Select error..."
                        : formData.work_type 
                          ? "Select error description..."
                          : "Select work type first..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0">
                    <Command>
                      <CommandInput placeholder="Search error description..." />
                      <CommandEmpty>
                        {formData.work_type 
                          ? `No errors found for ${formData.work_type} work type.` 
                          : "No error found."}
                      </CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {filteredErrorMaster.map((master) => (
                          <CommandItem
                            key={master.id}
                            value={master.description}
                            onSelect={() => {
                              handleErrorMasterChange(master.id);
                              setOpenError(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.error_master_id === master.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {master.description}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="error_type" className="text-md3-label-large text-gray-700 font-medium">Error Type (Auto-populated)</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container" id="error_type"
                  value={selectedErrorMaster ? `${selectedErrorMaster.error_type_name} (${selectedErrorMaster.weightage_points || 0} points)` : ""}
                  disabled
                  
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity" className="text-md3-label-large text-gray-700 font-medium">Quantity*</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="quantity"
                    type="number"
                    step="0.1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                    placeholder="e.g., 5"
                  />
                </div>
                <div>
                  <Label htmlFor="total_value" className="text-md3-label-large text-gray-700 font-medium">Total Value (Auto-calculated)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 bg-md3-surface-container" id="total_value"
                    value={calculateTotalValue().toFixed(2)}
                    disabled
                    
                  />
                </div>
              </div>

              {/* Conditional: Show Rework Hours for non-Estimation OR Impact for Estimation */}
              {formData.work_type === "Estimation" ? (
                <div>
                  <Label htmlFor="impact" className="text-md3-label-large text-gray-700 font-medium">Impact*</Label>
                  <Select
                    value={formData.impact}
                    onValueChange={(value) => setFormData({ ...formData, impact: value })}
                    required
                  >
                    <SelectTrigger id="impact">
                      <SelectValue placeholder="Select impact level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label htmlFor="rework_hours" className="text-md3-label-large text-gray-700 font-medium">Rework Hours (Enter later after fix)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="rework_hours"
                    type="number"
                    step="0.1"
                    value={formData.rework_hours}
                    onChange={(e) => setFormData({ ...formData, rework_hours: e.target.value })}
                    placeholder="e.g., 2.5"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editingLog ? "Update" : "Assign Error"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
